import Vue from "vue";
import ShopService from "@/services/ShopService";
import UserService from "@/services/UserService";

const state = () => ({
  followedShops: [],

  /*
   * Shop Page
   */
  shopProducts: [],
  shop: {},
  shopPage: 0,

  popular: [],
  spotify: [],
  recommended: [],
});

const getters = {
  getShopProducts: (state) => {
    return state.shopProducts;
  },
  getFollowedShops: (state) => {
    return state.followedShops;
  },
  hasFollowedShops: (state) => {
    return state.followedShops.length > 0;
  },
  isShopFollowed: (state) => (id) => {
    return state.followedShops.findIndex((s) => s.id === id) !== -1;
  },
  getShop: (state) => {
    return state.shop;
  },
  getShopsWithAlerts: (state) => {
    return state.followedShops.filter(
      (s) => s.userShops != null && s.userShops.allowAlerts
    );
  },
  doesShopAllowAlerts: (state) => (shop) => {
    const index = state.followedShops.findIndex((s) => s.id === shop.id);
    if (index > -1) {
      const shop = state.followedShops[index];
      if (shop.userShops != null) {
        return shop.userShops.allowAlerts;
      }
    }
    return false;
  },
  doesShopHaveNewProducts: (state) => (shop) => {
    const index = state.followedShops.findIndex((s) => s.id === shop.id);
    if (index > -1) {
      const shop = state.followedShops[index];
      if (shop.userShops != null) {
        return shop.userShops.newProductCount > 0;
      }
    }
    return false;
  },
  getFollowedShopFromId: (state) => (id) => {
    const index = state.followedShops.findIndex((s) => s.id === id);
    if (index > -1) {
      return state.followedShops[index];
    }
    return null;
  },
  getPopularShops: (state) => {
    return state.popular;
  },
  getSpotifyShops: (state) => {
    return state.spotify;
  },
  getRecommendedShops: (state) => {
    return state.recommended;
  },
};

const actions = {
  /*
   * Shop Page
   */
  load({ commit, state }, id) {
    if (state.shop && state.shop.id !== id) {
      commit("resetShopProducts");
    }
    const pageSize = 18;
    return ShopService.loadShop(id, {
      limit: pageSize,
      offset: state.shopPage * pageSize,
    }).then(
      (response) => {
        const shop = response.data;
        commit("setShop", shop);
        commit("shopNextPage");
        commit("appendShopProducts", shop.products);
        return Promise.resolve(shop);
      },
      (error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        return Promise.reject(message);
      }
    );
  },

  /*
   * Local following cache
   */
  localFollowShop({ commit }, shop) {
    commit("appendFollowedShop", shop);
  },
  localFollowShops({ commit }, shops) {
    const shopList = shops.map((shop) => shop);
    commit("setFollowedShops", shopList);
  },
  localUnfollowShop({ commit }, shop) {
    commit("removeFollowedShop", shop);
  },
  localUnfollowShops({ commit }, shops) {
    shops.forEach((shop) => {
      commit("removeFollowedShop", shop);
    });
  },

  /*
   * Following shops
   */
  followShop({ commit }, shop) {
    commit("appendFollowedShop", shop);
    return ShopService.followShop(shop.id);
  },
  unfollowShop({ commit }, shop) {
    commit("removeFollowedShop", shop);
    return ShopService.unfollowShop(shop.id);
  },
  followShops({ commit }, shops) {
    const shopList = shops.map((shop) => shop);
    const shopIdsList = shops.map((shop) => shop.id);
    commit("setFollowedShops", shopList);
    return ShopService.followShops(shopIdsList);
  },
  fetchFollowedShops({ commit }) {
    return ShopService.getFollowedShops().then(
      (response) => {
        const shops = response.data;
        commit("setFollowedShops", shops);
        return Promise.resolve(shops);
      },
      (error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        return Promise.reject(message);
      }
    );
  },

  /*
   * Alerts
   */
  muteAlertsForShop({ commit }, shop) {
    commit("setAlertsForShop", { shop: shop, alertsOn: false });
    UserService.muteShop(shop.id);
  },
  unmuteAlertsForShop({ commit }, shop) {
    commit("setAlertsForShop", { shop: shop, alertsOn: true });
    UserService.unmuteShop(shop.id);
  },
  muteAllShops({ commit, getters }) {
    getters.getShopsWithAlerts.forEach((shop) => {
      commit("setAlertsForShop", { shop: shop, alertsOn: false });
      UserService.muteShop(shop.id);
    });
  },

  /*
   * Notifications
   */
  clearNotificationsForShop({ commit }, shopId) {
    commit("clearNotificationsForShopId", shopId);
    UserService.clearBadge(shopId);
  },

  /*
   * Popular
   */
  fetchPopularShops({ commit, state }) {
    if (state.popular.length) {
      return Promise.resolve(state.popular);
    }

    return ShopService.getPopularShops({
      limit: 9,
    }).then(
      (response) => {
        const shops = response.data;
        commit("setPopularShops", shops);
        return Promise.resolve(shops);
      },
      (error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        return Promise.reject(message);
      }
    );
  },

  /*
   * Spotify
   */
  fetchSpotifyShops({ commit, state }) {
    if (state.spotify.length) {
      return Promise.resolve(state.spotify);
    }

    return ShopService.getSpotifyShops({
      limit: 9,
    }).then(
      (response) => {
        const shops = response.data;
        commit("setSpotifyShops", shops);
        return Promise.resolve(shops);
      },
      (error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        return Promise.reject(message);
      }
    );
  },

  /*
   * Recommended
   */
  fetchRecommendedShops({ commit, state }) {
    if (state.recommended.length) {
      return Promise.resolve(state.recommended);
    }

    return ShopService.getRecommendedShops({
      limit: 9,
    }).then(
      (response) => {
        const shops = response.data;
        commit("setRecommendedShops", shops);
        return Promise.resolve(shops);
      },
      (error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        return Promise.reject(message);
      }
    );
  },
};

const mutations = {
  /*
   * Shop Page
   */
  resetShopProducts(state) {
    state.shopProducts = [];
    state.shopPage = 0;
  },
  setShop(state, shop) {
    state.shop = shop;
  },
  appendShopProducts(state, products) {
    state.shopProducts.push(...products);
  },
  shopNextPage(state) {
    state.shopPage += 1;
  },

  setFollowedShops(state, shops) {
    state.followedShops = shops;
  },
  appendFollowedShop(state, shop) {
    if (shop.userShops == null) {
      shop.userShops = {
        allowAlerts: false,
        newProductCount: 0,
      };
    }
    state.followedShops.unshift(shop);
  },
  removeFollowedShop(state, shop) {
    const index = state.followedShops.findIndex((s) => s.id === shop.id);
    if (index > -1) {
      state.followedShops.splice(index, 1);
    }
  },
  setAlertsForShop(state, data) {
    const shop = data.shop;
    const alertsOn = data.alertsOn;
    const index = state.followedShops.findIndex((s) => s.id === shop.id);
    if (index > -1) {
      const updatedFollow = state.followedShops[index];
      if (updatedFollow.userShops != null) {
        updatedFollow.userShops.allowAlerts = alertsOn;
      } else {
        updatedFollow.userShops = { allowAlerts: alertsOn, newProductCount: 0 };
      }
      Vue.set(state.followedShops, index, updatedFollow);
    }
  },
  clearNotificationsForShopId(state, shopId) {
    const index = state.followedShops.findIndex((s) => s.id === shopId);
    if (index > -1) {
      const updatedFollow = state.followedShops[index];
      if (updatedFollow.userShops != null) {
        updatedFollow.userShops.newProductCount = 0;
        Vue.set(state.followedShops, index, updatedFollow);
      }
    }
  },

  setPopularShops(state, shops) {
    state.popular = shops;
  },
  setSpotifyShops(state, shops) {
    state.spotify = shops;
  },
  setRecommendedShops(state, shops) {
    state.recommended = shops;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
